import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Plus, Check, X, Briefcase, Eye, ExternalLink, ChevronDown,
  TrendingUp, TrendingDown, Minus, Sunrise, Moon,
  LineChart, Newspaper, FileText, Building2, Sparkles, Activity, Gauge,
  Globe, Users, MapPin, Calendar, BadgeDollarSign, BarChart3,
  ArrowUpRight, ArrowDownRight, Info, CircleGauge,
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  DoughnutController,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import TradeView from './TradeView';
import {
  getCachedPortfolioMap, setCachedPortfolioMap, updateCachedPortfolioMap,
  clearCachedPortfolioData,
  getCachedWatchlistMap, setCachedWatchlistMap, updateCachedWatchlistMap, clearCachedWatchlistData,
} from '../utils/portfolioWatchlistCache';
import { getCachedTradeDates } from '../utils/tradeDatesCache';
import { applyLiveQuoteToStock, getNyMarketSession } from '../utils/liveMarketData';
import { authFetch, getAccessToken } from '../utils/authFetch';
import { useLiveMarketDisplay } from '../hooks/useLiveMarketDisplay';
import { useTheme } from '../utils/theme';
import '../styles.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  DoughnutController,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// Period sort: full year = FY; quarterly = Q1..Q4. Order quarters Q4, Q3, Q2, Q1 within same year.
const QUARTER_ORDER = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };
const isFullYear = (period) => !period || String(period).toUpperCase() === 'FY' || String(period).toLowerCase() === 'full year';

const sortPeriods = (rows) => {
  if (!rows || !rows.length) return { fullYear: [], quarterly: [] };
  const fullYear = rows
    .filter((r) => isFullYear(r.period))
    .sort((a, b) => (Number(b.fiscal_year) || 0) - (Number(a.fiscal_year) || 0))
    .slice(0, 5);
  const quarterly = rows
    .filter((r) => !isFullYear(r.period) && /Q[1-4]/i.test(String(r.period || '')))
    .sort((a, b) => {
      const yA = Number(a.fiscal_year) || 0;
      const yB = Number(b.fiscal_year) || 0;
      if (yB !== yA) return yB - yA;
      const qA = QUARTER_ORDER[String(a.period || '').toUpperCase().replace(/\s/g, '')] || 0;
      const qB = QUARTER_ORDER[String(b.period || '').toUpperCase().replace(/\s/g, '')] || 0;
      return qB - qA;
    })
    .slice(0, 5);
  return { fullYear, quarterly };
};

const formatFiscalYear = (fy) => {
  if (fy == null || fy === '') return '--';
  const n = Number(fy);
  if (Number.isNaN(n)) return String(fy);
  return String(Math.floor(n));
};

const formatPeriodLabel = (row) => {
  const fy = formatFiscalYear(row.fiscal_year);
  const p = String(row.period || '').trim().toUpperCase();
  if (isFullYear(row.period) || !p || p === 'FY') return fy;
  return `${fy} ${p}`;
};

const formatFinancialValue = (value) => {
  if (value == null || value === '') return '--';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  if (Math.abs(n) >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const META_KEYS = new Set(['date', 'symbol', 'reported_currency', 'cik', 'filing_date', 'accepted_date', 'fiscal_year', 'period']);

const snakeToLabel = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// All income statement fields (order from schema)
const INCOME_KEYS = [
  'revenue', 'cost_of_revenue', 'gross_profit', 'research_and_development_expenses',
  'general_and_administrative_expenses', 'selling_and_marketing_expenses', 'selling_general_and_administrative_expenses',
  'other_expenses', 'operating_expenses', 'cost_and_expenses', 'net_interest_income', 'interest_income', 'interest_expense',
  'depreciation_and_amortization', 'ebitda', 'ebit', 'non_operating_income_excluding_interest', 'operating_income',
  'total_other_income_expenses_net', 'income_before_tax', 'income_tax_expense', 'net_income_from_continuing_operations',
  'net_income_from_discontinued_operations', 'other_adjustments_to_net_income', 'net_income', 'net_income_deductions',
  'bottom_line_net_income', 'eps', 'eps_diluted', 'weighted_average_shs_out', 'weighted_average_shs_out_dil'
];
const BALANCE_KEYS = [
  'cash_and_cash_equivalents', 'short_term_investments', 'cash_and_short_term_investments', 'net_receivables',
  'accounts_receivables', 'other_receivables', 'inventory', 'prepaids', 'other_current_assets', 'total_current_assets',
  'property_plant_equipment_net', 'goodwill', 'intangible_assets', 'goodwill_and_intangible_assets', 'long_term_investments',
  'tax_assets', 'other_non_current_assets', 'total_non_current_assets', 'other_assets', 'total_assets',
  'total_payables', 'account_payables', 'other_payables', 'accrued_expenses', 'short_term_debt',
  'capital_lease_obligations_current', 'tax_payables', 'deferred_revenue', 'other_current_liabilities', 'total_current_liabilities',
  'long_term_debt', 'capital_lease_obligations_non_current', 'deferred_revenue_non_current', 'deferred_tax_liabilities_non_current',
  'other_non_current_liabilities', 'total_non_current_liabilities', 'other_liabilities', 'capital_lease_obligations', 'total_liabilities',
  'treasury_stock', 'preferred_stock', 'common_stock', 'retained_earnings', 'additional_paid_in_capital',
  'accumulated_other_comprehensive_income_loss', 'other_total_stockholders_equity', 'total_stockholders_equity', 'total_equity',
  'minority_interest', 'total_liabilities_and_total_equity', 'total_investments', 'total_debt', 'net_debt'
];
const CASHFLOW_KEYS = [
  'net_income', 'depreciation_and_amortization', 'deferred_income_tax', 'stock_based_compensation', 'change_in_working_capital',
  'accounts_receivables', 'inventory', 'accounts_payables', 'other_working_capital', 'other_non_cash_items',
  'net_cash_provided_by_operating_activities', 'investments_in_property_plant_and_equipment', 'acquisitions_net',
  'purchases_of_investments', 'sales_maturities_of_investments', 'other_investing_activities', 'net_cash_provided_by_investing_activities',
  'net_debt_issuance', 'long_term_net_debt_issuance', 'short_term_net_debt_issuance', 'net_stock_issuance', 'net_common_stock_issuance',
  'common_stock_issuance', 'common_stock_repurchased', 'net_preferred_stock_issuance', 'net_dividends_paid', 'common_dividends_paid',
  'preferred_dividends_paid', 'other_financing_activities', 'net_cash_provided_by_financing_activities', 'effect_of_forex_changes_on_cash',
  'net_change_in_cash', 'cash_at_end_of_period', 'cash_at_beginning_of_period', 'operating_cash_flow', 'capital_expenditure',
  'free_cash_flow', 'income_taxes_paid', 'interest_paid'
];

function getLineItemsForReport(reportKey, periods) {
  const keyList = reportKey === 'income_statement' ? INCOME_KEYS : reportKey === 'balance_sheet' ? BALANCE_KEYS : CASHFLOW_KEYS;
  const keysInData = new Set();
  if (periods && periods.length) {
    periods.forEach((p) => Object.keys(p).filter((k) => !META_KEYS.has(k)).forEach((k) => keysInData.add(k)));
  }
  const ordered = keyList.filter((k) => keysInData.has(k));
  const rest = [...keysInData].filter((k) => !keyList.includes(k)).sort();
  return [...ordered, ...rest].map((key) => ({ key, label: snakeToLabel(key) }));
}

// Key metrics groups: category label -> array of field keys (metric_ratio_group / Chk.js schema; camelCase → snake_case)
const KEY_METRICS_GROUPS = [
  {
    label: 'Profitability',
    keys: ['return_on_equity', 'return_on_assets', 'gross_profit_margin', 'net_profit_margin', 'return_on_invested_capital'],
  },
  {
    label: 'Liquidity',
    keys: ['current_ratio', 'quick_ratio', 'cash_ratio', 'operating_cash_flow_ratio', 'working_capital'],
  },
  // Efficiency: omit inventory + turnover ratios (redundant with DSO/DPO for fundamentals)
  {
    label: 'Efficiency',
    keys: [
      'asset_turnover', 'days_of_sales_outstanding', 'days_of_payables_outstanding',
      'cash_conversion_cycle', 'operating_cycle',
    ],
  },
  {
    label: 'Leverage And Valuation',
    keys: [
      'debt_to_assets_ratio', 'debt_to_equity_ratio', 'price_to_book_ratio', 'price_to_earnings_growth_ratio',
      'forward_price_to_earnings_growth_ratio', 
    ],
  },
];
const KEY_METRICS_ROW_LAYOUT = [['Profitability'], ['Liquidity'], ['Efficiency'], ['Leverage And Valuation']];

function MemberSignupCTA({ title, description }) {
  return (
    <div className="sd-tab-empty sd-member-cta">
      <Sparkles size={26} className="sd-tab-empty-icon" />
      <h4>{title}</h4>
      <p>{description}</p>
      <div className="sd-member-cta-actions">
        <Link to="/login" className="sd-member-cta-btn" aria-label="Log in or sign up">
          Log in <span className="sd-member-cta-sep" aria-hidden="true">|</span> Sign up
        </Link>
      </div>
    </div>
  );
}

function AIOverviewTab({ data, loading, error, guestGated }) {
  if (loading) {
    return (
      <div className="sd-tab-state">
        <div className="sd-skeleton-block sd-skeleton-block--lg" />
        <div className="sd-skeleton-block" />
        <div className="sd-skeleton-block" />
      </div>
    );
  }
  if (guestGated) {
    return (
      <MemberSignupCTA
        title="AI overview for members"
        description="Create a free account to unlock AI-generated company summaries, sentiment, and key drivers."
      />
    );
  }
  if (error) return <p className="sd-tab-error">{error}</p>;
  if (!data) {
    return (
      <div className="sd-ai-empty">
        <Sparkles size={28} className="sd-ai-empty-icon" />
        <h4>AI overview not available yet</h4>
        <p>Symbols currently supported include stocks on the S&P 500, NASDAQ 100, DOW 30 & S&P|TSX Comp. Indices</p>
      </div>
    );
  }

  const topRevenueExpenseDrivers = data.top_revenue_expense_drivers || [];
  const keyDrivers = data.key_drivers || [];
  const tailwinds = data.tailwinds || [];
  const headwinds = data.headwinds || [];
  const shortSentiment = (data.short_term_sentiment || '').toLowerCase();
  const longSentiment = (data.long_term_sentiment || '').toLowerCase();

  const sentimentInfo = (sentiment) => {
    if (sentiment === 'bullish') {
      return { className: 'sd-sentiment-bullish', icon: <TrendingUp size={14} />, label: 'Bullish' };
    }
    if (sentiment === 'bearish') {
      return { className: 'sd-sentiment-bearish', icon: <TrendingDown size={14} />, label: 'Bearish' };
    }
    if (sentiment === 'neutral') {
      return { className: 'sd-sentiment-neutral', icon: <Minus size={14} />, label: 'Neutral' };
    }
    return { className: 'sd-sentiment-neutral', icon: <Minus size={14} />, label: '—' };
  };

  const renderBullets = (arr) => {
    if (!arr || !arr.length) return <p className="sd-ai-list-empty">—</p>;
    return (
      <ul className="sd-ai-list">
        {arr.map((item, i) => (
          <li key={i} className="sd-ai-list-item">
            <span className="sd-ai-list-key">{item.bullet_point}:</span>
            <span className="sd-ai-list-detail"> {item.details}</span>
          </li>
        ))}
      </ul>
    );
  };

  const shortInfo = sentimentInfo(shortSentiment);
  const longInfo = sentimentInfo(longSentiment);

  return (
    <div className="sd-ai-overview">
      <div className="sd-ai-summary-card">
        <div className="sd-ai-summary-header">
          <Sparkles size={16} className="sd-ai-icon" />
          <span>Summary</span>
        </div>
        <p className="sd-ai-summary-text">{data.summary || '—'}</p>
        <div className="sd-ai-sentiment-row">
          <div className="sd-ai-sentiment-cell">
            <span className="sd-ai-sentiment-label">Short-term outlook</span>
            <span className={`sd-sentiment-badge ${shortInfo.className}`}>
              {shortInfo.icon} {shortInfo.label}
            </span>
          </div>
          <div className="sd-ai-sentiment-cell">
            <span className="sd-ai-sentiment-label">Long-term outlook</span>
            <span className={`sd-sentiment-badge ${longInfo.className}`}>
              {longInfo.icon} {longInfo.label}
            </span>
          </div>
        </div>
      </div>

      <div className="sd-ai-grid">
        <div className="sd-ai-section">
          <div className="sd-ai-section-title sd-ai-section-title--neutral">
            <Activity size={14} /> Key drivers
          </div>
          {renderBullets(keyDrivers)}
        </div>
        <div className="sd-ai-section">
          <div className="sd-ai-section-title sd-ai-section-title--info">
            <BadgeDollarSign size={14} /> Top revenue and expense drivers
          </div>
          {renderBullets(topRevenueExpenseDrivers)}
        </div>
        <div className="sd-ai-section">
          <div className="sd-ai-section-title sd-ai-section-title--bullish">
            <ArrowUpRight size={14} /> Tailwinds
          </div>
          {renderBullets(tailwinds)}
        </div>
        <div className="sd-ai-section">
          <div className="sd-ai-section-title sd-ai-section-title--bearish">
            <ArrowDownRight size={14} /> Headwinds
          </div>
          {renderBullets(headwinds)}
        </div>
      </div>
    </div>
  );
}

function KeyMetricsTab({ data, loading, error, formatFinancialValue, guestGated }) {
  if (loading) {
    return (
      <div className="sd-tab-state">
        <div className="sd-skeleton-block sd-skeleton-block--lg" />
        <div className="sd-skeleton-block" />
      </div>
    );
  }
  if (guestGated) {
    return (
      <MemberSignupCTA
        title="Key metrics for members"
        description="Sign in to view profitability, liquidity, efficiency, and valuation ratios for this symbol."
      />
    );
  }
  if (error) return <p className="sd-tab-error">{error}</p>;
  if (!data || !data.metric_ratio || !data.metric_ratio.length) {
    return (
      <div className="sd-tab-empty">
        <Gauge size={26} className="sd-tab-empty-icon" />
        <p>No metric ratio data available.</p>
      </div>
    );
  }
  const rows = data.metric_ratio;
  const latest = [...rows].sort((a, b) => {
    const y = (Number(b.fiscal_year) || 0) - (Number(a.fiscal_year) || 0);
    if (y !== 0) return y;
    const qOrder = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };
    const qA = qOrder[String(a.period || '').toUpperCase().replace(/\s/g, '')] || 0;
    const qB = qOrder[String(b.period || '').toUpperCase().replace(/\s/g, '')] || 0;
    return qB - qA;
  })[0];

  const formatRatio = (value, key) => {
    if (value == null || value === '') return '—';
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    if (key && (key.includes('margin') || key.includes('return_on'))) {
      return `${n.toFixed(2)}%`;
    }
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const GROUP_ICONS = {
    'Profitability': <Activity size={15} />,
    'Liquidity': <Gauge size={15} />,
    'Efficiency': <BarChart3 size={15} />,
    'Leverage And Valuation': <BadgeDollarSign size={15} />,
  };

  const periodLabel = formatPeriodLabel(latest);

  return (
    <div className="sd-summary-grid">
      {KEY_METRICS_GROUPS.map((group) => (
        <div key={group.label} className="sd-summary-group">
          <div className="sd-summary-group-title">
            <span className="sd-summary-group-icon">{GROUP_ICONS[group.label]}</span>
            {group.label}
          </div>
          <div className="sd-summary-stat-list">
            {group.keys.map((key) => {
              const val = latest[key];
              const displayVal =
                key === 'working_capital' ? formatFinancialValue(val) : formatRatio(val, key);
              return (
                <div key={key} className="sd-summary-stat">
                  <span className="sd-summary-stat-label">{snakeToLabel(key)}</span>
                  <span className="sd-summary-stat-value">{displayVal}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function FinancialPeriodTable({ periods, lineItems }) {
  if (!periods || periods.length === 0) return null;
  return (
    <div className="financial-period-block">
      <div className="financial-table-wrap">
        <table className="financial-table">
          <thead>
            <tr>
              <th className="financial-table-metric">Metric</th>
              {periods.map((p, i) => (
                <th key={i} className="financial-table-period">
                  {formatPeriodLabel(p)}
                  {p.filing_date && (
                    <div className="financial-filing-date">{p.filing_date}</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lineItems.map(({ key, label }) => (
              <tr key={key}>
                <td className="financial-table-metric">{label}</td>
                {periods.map((p, i) => (
                  <td key={i} className="financial-table-value">{formatFinancialValue(p[key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const CHART_COLORS = {
  blue: 'rgba(13, 71, 161, 0.85)',
  blueFill: 'rgba(13, 71, 161, 0.15)',
  green: 'rgba(46, 125, 50, 0.85)',
  greenFill: 'rgba(46, 125, 50, 0.15)',
  red: 'rgba(198, 40, 40, 0.85)',
  redFill: 'rgba(198, 40, 40, 0.15)',
  amber: 'rgba(245, 124, 0, 0.85)',
  amberFill: 'rgba(245, 124, 0, 0.15)',
};

/** Chart.js text/grid colors tuned for light vs dark theme contrast. */
const chartThemeColors = (isDark) => (
  isDark
    ? {
      title: '#9adcf0',
      label: '#eaf0f8',
      tick: '#cdd6e3',
      grid: 'rgba(255,255,255,0.10)',
      donutBorder: '#161f2e',
    }
    : {
      title: '#0b3d91',
      label: '#0f172a',
      tick: '#334155',
      grid: 'rgba(15,23,42,0.10)',
      donutBorder: '#ffffff',
    }
);

const chartBaseOptions = (titleText, yTickCb, isDark = false) => {
  const c = chartThemeColors(isDark);
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 8, padding: 6, font: { size: 9 }, color: c.label },
      },
      title: {
        display: true,
        text: titleText,
        font: { size: 11, weight: '600' },
        color: c.title,
        padding: { bottom: 4 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed.y;
            if (v == null) return '';
            if (yTickCb) return `${ctx.dataset.label}: ${yTickCb(v)}`;
            return `${ctx.dataset.label}: ${formatFinancialValue(v)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 9 }, maxRotation: 0, color: c.tick },
      },
      y: {
        grid: { color: c.grid },
        ticks: {
          font: { size: 9 },
          maxTicksLimit: 5,
          color: c.tick,
          callback: yTickCb || ((v) => formatFinancialValue(v)),
        },
      },
    },
  };
};

function FundamentalChart({ type, data, options }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !data) return;
    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new ChartJS(canvasRef.current, { type, data, options });
    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [type, data, options]);

  return <canvas ref={canvasRef} />;
}

const pctTick = (v) => `${v.toFixed(1)}%`;

const QUARTER_LABELS = ['Q1', 'Q2', 'Q3', 'Q4'];
const extractQuarterCode = (period) => {
  const m = String(period || '').toUpperCase().replace(/\s/g, '').match(/Q[1-4]/);
  return m ? m[0] : null;
};

/** Last N fiscal quarters, oldest → newest. */
function getQuarterlyRowsChronological(rows, maxCount = 8) {
  if (!rows || !rows.length) return [];
  const quarterly = rows
    .filter((r) => !isFullYear(r.period) && extractQuarterCode(r.period))
    .sort((a, b) => {
      const yA = Number(a.fiscal_year) || 0;
      const yB = Number(b.fiscal_year) || 0;
      if (yA !== yB) return yA - yB;
      const qA = QUARTER_ORDER[extractQuarterCode(a.period)] || 0;
      const qB = QUARTER_ORDER[extractQuarterCode(b.period)] || 0;
      return qA - qB;
    });
  return quarterly.slice(-maxCount);
}

/** All quarterly rows for income (or other reports), oldest → newest. */
function getQuarterlyRowsAll(rows) {
  if (!rows || !rows.length) return [];
  return rows
    .filter((r) => !isFullYear(r.period) && extractQuarterCode(r.period))
    .sort((a, b) => {
      const yA = Number(a.fiscal_year) || 0;
      const yB = Number(b.fiscal_year) || 0;
      if (yA !== yB) return yA - yB;
      const qA = QUARTER_ORDER[extractQuarterCode(a.period)] || 0;
      const qB = QUARTER_ORDER[extractQuarterCode(b.period)] || 0;
      return qA - qB;
    });
}

function fiscalYearsDescendingFromQuarters(quarterlyRows) {
  return [...new Set(quarterlyRows.map((r) => Number(r.fiscal_year) || 0))]
    .filter((y) => y > 0)
    .sort((a, b) => b - a);
}

function revenueForQuarterFY(rows, fy, qLabel) {
  const r = rows.find(
    (row) => (Number(row.fiscal_year) || 0) === fy
      && extractQuarterCode(row.period) === qLabel,
  );
  if (!r) return null;
  const v = Number(r.revenue);
  return Number.isFinite(v) ? v : null;
}

function colorForFiscalYearRank(rank) {
  if (rank <= 0) return CHART_COLORS.green; // newest year
  if (rank === 1) return CHART_COLORS.blue; // second newest year
  return CHART_COLORS.amber; // older fallback years
}

function doughnutChartOptions(titleText, meta = {}, isDark = false) {
  const { totalAssets } = meta;
  const c = chartThemeColors(isDark);
  return {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '54%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 8, padding: 4, font: { size: 8 }, color: c.label },
      },
      title: {
        display: true,
        text: titleText,
        font: { size: 10, weight: '600' },
        color: c.title,
        padding: { bottom: 2 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const v = Number(ctx.raw);
            if (!Number.isFinite(v)) return '';
            const arr = ctx.chart.data.datasets[0].data.map((x) => Number(x));
            const total = arr.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
            const pct = total > 0 ? ((v / total) * 100).toFixed(1) : '0';
            const lines = [`${ctx.label}: ${formatFinancialValue(v)} (${pct}% of chart total)`];
            if (totalAssets != null && Number.isFinite(totalAssets) && totalAssets > 0) {
              lines.push(`≈ ${((v / totalAssets) * 100).toFixed(1)}% of total assets`);
            }
            return lines;
          },
        },
      },
    },
  };
}

/** Liabilities vs equity (latest quarter) — financing KPI. */
function buildCapitalStructureDonut(financialsData) {
  const q = getQuarterlyRowsChronological(financialsData.balance_sheet || [], 24);
  if (!q.length) return null;
  const lq = q[q.length - 1];
  const liab = Number(lq.total_liabilities);
  const eq = Number(lq.total_stockholders_equity);
  const assets = Number(lq.total_assets);
  if (!Number.isFinite(liab) || !Number.isFinite(eq) || liab < 0 || eq < 0) return null;
  if (liab + eq <= 0) return null;
  return {
    title: 'Capital structure (latest quarter)',
    labels: ['Total liabilities', 'Stockholders\' equity'],
    data: [liab, eq],
    colors: [CHART_COLORS.red, CHART_COLORS.green],
    totalAssets: Number.isFinite(assets) && assets > 0 ? assets : null,
  };
}

function buildAssetsCompositionDonut(financialsData) {
  const q = getQuarterlyRowsChronological(financialsData.balance_sheet || [], 24);
  if (!q.length) return null;
  const lq = q[q.length - 1];
  const cur = Number(lq.total_current_assets);
  const ncur = Number(lq.total_non_current_assets);
  const assets = Number(lq.total_assets);
  if (!Number.isFinite(cur) || !Number.isFinite(ncur) || cur + ncur <= 0) return null;
  return {
    title: 'Asset mix (latest quarter)',
    labels: ['Current assets', 'Non-current assets'],
    data: [cur, ncur],
    colors: [CHART_COLORS.blue, CHART_COLORS.amber],
    totalAssets: Number.isFinite(assets) && assets > 0 ? assets : null,
  };
}

function FundamentalCharts({ financialsData }) {
  const { mode: themeMode } = useTheme();
  const isDark = themeMode === 'dark';

  const charts = useMemo(() => {
    if (!financialsData) return [];
    const theme = chartThemeColors(isDark);

    const leadingCharts = [];

    const incomeAllQ = getQuarterlyRowsAll(financialsData.income_statement || []);
    const fyDesc = fiscalYearsDescendingFromQuarters(incomeAllQ);
    if (incomeAllQ.length >= 1 && fyDesc.length >= 1) {
      const labels = QUARTER_LABELS;
      const fyColorMap = Object.fromEntries(
        fyDesc.map((fy, idx) => [String(fy), colorForFiscalYearRank(idx)]),
      );
      const quarterPicks = QUARTER_LABELS.map((q) => {
        const available = fyDesc
          .map((fy, idx) => ({ fy, rank: idx, value: revenueForQuarterFY(incomeAllQ, fy, q) }))
          .filter((x) => x.value != null)
          .slice(0, 2);
        return {
          first: available[0] || null,
          second: available[1] || null,
        };
      });
      const datasets = [
        {
          label: 'Previous Quarter',
          data: quarterPicks.map((p) => (p.second ? p.second.value : null)),
          backgroundColor: quarterPicks.map((p) => (p.second ? fyColorMap[String(p.second.fy)] : 'rgba(150,150,150,0.18)')),
          borderRadius: 2,
        },
        {
          label: 'Latest Quarter',
          data: quarterPicks.map((p) => (p.first ? p.first.value : null)),
          backgroundColor: quarterPicks.map((p) => (p.first ? fyColorMap[String(p.first.fy)] : 'rgba(150,150,150,0.25)')),
          borderRadius: 2,
        },
      ];
      const yearsInUse = [...new Set(quarterPicks.flatMap((p) => [p.first?.fy, p.second?.fy]).filter((y) => y != null))];
      const base = chartBaseOptions('Revenue - Quarter over Quarter', null, isDark);
      leadingCharts.push({
        type: 'bar',
        data: { labels, datasets },
        options: {
          ...base,
          plugins: {
            ...base.plugins,
            legend: {
              ...base.plugins.legend,
              labels: {
                ...base.plugins.legend.labels,
                color: theme.label,
                generateLabels: () => yearsInUse.map((fy) => ({
                  text: formatFiscalYear(fy),
                  fillStyle: fyColorMap[String(fy)],
                  strokeStyle: fyColorMap[String(fy)],
                  fontColor: theme.label,
                  lineWidth: 0,
                  hidden: false,
                  datasetIndex: -1,
                })),
              },
            },
            tooltip: {
              ...base.plugins.tooltip,
              callbacks: {
                label: (ctx) => {
                  const row = quarterPicks[ctx.dataIndex];
                  const pick = ctx.datasetIndex === 0 ? row?.second : row?.first;
                  if (!pick || pick.value == null) return `${ctx.dataset.label}: --`;
                  return `${ctx.dataset.label} (${formatFiscalYear(pick.fy)}): ${formatFinancialValue(pick.value)}`;
                },
              },
            },
          },
          scales: {
            ...base.scales,
            x: {
              ...base.scales.x,
              ticks: { ...base.scales.x.ticks, maxRotation: 0, font: { size: 9 }, color: theme.tick },
            },
          },
        },
      });
    }

    const donutSpec = buildCapitalStructureDonut(financialsData) || buildAssetsCompositionDonut(financialsData);
    if (donutSpec) {
      leadingCharts.push({
        type: 'doughnut',
        data: {
          labels: donutSpec.labels,
          datasets: [{
            data: donutSpec.data,
            backgroundColor: donutSpec.colors,
            borderColor: theme.donutBorder,
            borderWidth: 1,
          }],
        },
        options: doughnutChartOptions(donutSpec.title, { totalAssets: donutSpec.totalAssets }, isDark),
      });
    }

    const getFullYearAsc = (rows) => {
      if (!rows || !rows.length) return [];
      return rows
        .filter((r) => isFullYear(r.period))
        .sort((a, b) => (Number(a.fiscal_year) || 0) - (Number(b.fiscal_year) || 0))
        .slice(-5);
    };

    const incomeRows = getFullYearAsc(financialsData.income_statement || []);
    const balanceRows = getFullYearAsc(financialsData.balance_sheet || []);
    const cashflowRows = getFullYearAsc(financialsData.cashflow || []);

    if (!incomeRows.length && !balanceRows.length && !cashflowRows.length) {
      return leadingCharts;
    }

    const incomeLabels = incomeRows.map((r) => formatFiscalYear(r.fiscal_year));
    const balanceLabels = balanceRows.map((r) => formatFiscalYear(r.fiscal_year));
    const cashflowLabels = cashflowRows.map((r) => formatFiscalYear(r.fiscal_year));

    const revenueNetIncomeData = incomeRows.length ? {
      labels: incomeLabels,
      datasets: [
        { label: 'Revenue', data: incomeRows.map((r) => r.revenue), backgroundColor: CHART_COLORS.blue, borderRadius: 3 },
        { label: 'Net Income', data: incomeRows.map((r) => r.net_income ?? r.bottom_line_net_income), backgroundColor: CHART_COLORS.green, borderRadius: 3 },
      ],
    } : null;

    const marginData = incomeRows.length ? {
      labels: incomeLabels,
      datasets: [
        {
          label: 'Gross Margin',
          data: incomeRows.map((r) => r.revenue ? ((r.gross_profit || 0) / r.revenue) * 100 : null),
          borderColor: CHART_COLORS.blue, backgroundColor: CHART_COLORS.blueFill,
          tension: 0.3, fill: true, pointRadius: 3,
        },
        {
          label: 'Net Margin',
          data: incomeRows.map((r) => r.revenue ? (((r.net_income ?? r.bottom_line_net_income) || 0) / r.revenue) * 100 : null),
          borderColor: CHART_COLORS.green, backgroundColor: CHART_COLORS.greenFill,
          tension: 0.3, fill: true, pointRadius: 3,
        },
      ],
    } : null;

    const balanceData = balanceRows.length ? {
      labels: balanceLabels,
      datasets: [
        { label: 'Total Assets', data: balanceRows.map((r) => r.total_assets), backgroundColor: CHART_COLORS.blue, borderRadius: 3 },
        { label: 'Total Liabilities', data: balanceRows.map((r) => r.total_liabilities), backgroundColor: CHART_COLORS.red, borderRadius: 3 },
        { label: 'Stockholders\' Equity', data: balanceRows.map((r) => r.total_stockholders_equity), backgroundColor: CHART_COLORS.green, borderRadius: 3 },
      ],
    } : null;

    const cashflowData = cashflowRows.length ? {
      labels: cashflowLabels,
      datasets: [
        { label: 'Operating Cash Flow', data: cashflowRows.map((r) => r.operating_cash_flow ?? r.net_cash_provided_by_operating_activities), backgroundColor: CHART_COLORS.blue, borderRadius: 3 },
        { label: 'Capital Expenditure', data: cashflowRows.map((r) => r.capital_expenditure ?? r.investments_in_property_plant_and_equipment), backgroundColor: CHART_COLORS.amber, borderRadius: 3 },
        { label: 'Free Cash Flow', data: cashflowRows.map((r) => r.free_cash_flow), backgroundColor: CHART_COLORS.green, borderRadius: 3 },
      ],
    } : null;

    const snapshotCharts = [
      revenueNetIncomeData && { type: 'bar', data: revenueNetIncomeData, options: chartBaseOptions('Revenue & Net Income', null, isDark) },
      marginData && { type: 'line', data: marginData, options: chartBaseOptions('Profit Margins', pctTick, isDark) },
      balanceData && { type: 'bar', data: balanceData, options: chartBaseOptions('Assets, Liabilities & Equity', null, isDark) },
      cashflowData && { type: 'bar', data: cashflowData, options: chartBaseOptions('Cash Flow', null, isDark) },
    ].filter(Boolean);

    // Positions 1–2: first snapshot pair; 3–4: quarterly revenue + donut; 5–6: remaining annual charts (single-row layout).
    return [...snapshotCharts.slice(0, 2), ...leadingCharts, ...snapshotCharts.slice(2)];
  }, [financialsData, isDark]);

  if (!charts.length) return null;

  return (
    <section className="sd-card sd-charts-card">
      <div className="sd-card-header">
        <h3 className="sd-card-title">
          <BarChart3 size={18} className="sd-card-title-icon" />
          Financial Snapshot
        </h3>
      </div>
      <div className="stock-fundamental-charts-grid">
        {charts.map((cfg, i) => (
          <div key={i} className="fundamental-chart-card fundamental-chart-card--compact">
            <FundamentalChart type={cfg.type} data={cfg.data} options={cfg.options} />
          </div>
        ))}
      </div>
    </section>
  );
}

function AddToListDialog({ symbolId, displaySymbol, currentPrice, onClose }) {
  const MAX_PORTFOLIOS = 2;
  const MAX_WATCHLISTS = 3;
  const MAX_SYMBOLS_PER_LIST = 10;
  const [step, setStep] = useState('choose'); // 'choose' | 'selectList' | 'createNew' | 'success'
  const [listType, setListType] = useState(null); // 'portfolio' | 'watchlist'
  const [existingLists, setExistingLists] = useState([]);
  const [existingListMap, setExistingListMap] = useState({});
  const [selectedList, setSelectedList] = useState(null);
  const [newListName, setNewListName] = useState('');
  const [buyPrice, setBuyPrice] = useState(currentPrice ? String(Number(currentPrice).toFixed(2)) : '');
  const [qty, setQty] = useState('1');
  const [actionType, setActionType] = useState('long');
  const [buyDate, setBuyDate] = useState(null);
  const [allTradeDates, setAllTradeDates] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [addedListName, setAddedListName] = useState('');
  const [lastAction, setLastAction] = useState('added'); // 'added' | 'updated' | 'removed'
  const [pendingRemoveConfirm, setPendingRemoveConfirm] = useState(null);

  const [user] = useState(() => JSON.parse(localStorage.getItem('user')));

  useEffect(() => {
    const loadTradeDates = async () => {
      try {
        const dates = await getCachedTradeDates();
        setAllTradeDates(dates.map(d => new Date(d + 'T00:00:00')));
      } catch { /* ignore */ }
    };
    loadTradeDates();
  }, []);

  const fetchLists = async (type) => {
    if (!user) return;
    try {
      if (type === 'portfolio') {
        const cached = getCachedPortfolioMap(user.email);
        if (cached) {
          setExistingListMap(cached);
          setExistingLists(Object.keys(cached));
          return;
        }
        const res = await authFetch(`/api/portfolio/${user.email}`);
        const data = await res.json();
        const map = data.portfolioMap || {};
        setCachedPortfolioMap(user.email, map);
        setExistingListMap(map);
        setExistingLists(Object.keys(map));
      } else {
        const cached = getCachedWatchlistMap(user.email);
        if (cached) {
          setExistingListMap(cached);
          setExistingLists(Object.keys(cached));
          return;
        }
        const res = await authFetch(`/api/watchlist/${user.email}`);
        const data = await res.json();
        const map = data.watchlistMap || {};
        setCachedWatchlistMap(user.email, map);
        setExistingListMap(map);
        setExistingLists(Object.keys(map));
      }
    } catch {
      setExistingListMap({});
      setExistingLists([]);
    }
  };

  const handleChooseType = (type) => {
    setListType(type);
    setErrorMsg('');
    setSelectedList(null);
    setExistingListMap({});
    fetchLists(type);
    setStep('selectList');
  };

  const getListSymbolCount = (name) => {
    const listData = existingListMap[name];
    if (!listData) return 0;
    if (Array.isArray(listData)) return listData.length;
    if (typeof listData === 'object') return Object.keys(listData).length;
    return 0;
  };

  const availableLists = existingLists.filter((name) => getListSymbolCount(name) < MAX_SYMBOLS_PER_LIST);
  const allExistingListsAreFull = existingLists.length > 0 && availableLists.length === 0;
  const canCreateNewList = listType === 'portfolio'
    ? existingLists.length < MAX_PORTFOLIOS
    : existingLists.length < MAX_WATCHLISTS;

  const generateDefaultName = (type) => {
    const prefix = type === 'portfolio' ? 'portfolio_' : 'watchlist_';
    const existing = existingLists.filter(n => n.startsWith(prefix));
    const nextNum = existing.length > 0
      ? Math.max(...existing.map(n => {
          const m = n.match(new RegExp(`${prefix}(\\d+)`));
          return m ? parseInt(m[1]) : 0;
        })) + 1
      : 1;
    return `${prefix}${nextNum}`;
  };

  const getExistingEntry = (listName) => {
    if (!listName || listType !== 'portfolio') return null;
    const positions = existingListMap[listName];
    if (!positions || typeof positions !== 'object') return null;
    return positions[`${symbolId}|${actionType}`] || null;
  };

  const canSubmitPortfolio = (listName) => {
    const quantity = parseInt(qty, 10);
    if (isNaN(quantity) || quantity === 0) return false;
    const existingEntry = listName ? getExistingEntry(listName) : null;
    if (existingEntry) return true;
    return quantity >= 1 && buyDate && buyPrice;
  };

  const renderPortfolioFields = (listNameForHint) => {
    const existingEntry = listNameForHint ? getExistingEntry(listNameForHint) : null;
    const existingQty = existingEntry ? parseInt(existingEntry[3], 10) : null;
    const deltaQty = parseInt(qty, 10) || 0;
    const projectedQty = existingQty !== null ? existingQty + deltaQty : null;
    const isExisting = existingEntry != null;

    return (
      <div className="add-dialog-portfolio-section">
        <div className="pw-position-rows">
          <div className="pw-position-row-wrap">
            <div className="pw-position-row-label">
              {isExisting ? (
                <span className="pw-pos-hint pw-pos-existing">
                  Existing · {existingQty} shares @ {parseFloat(existingEntry[0]).toFixed(2)}
                  {projectedQty !== null && (
                    <span className={`pw-pos-projected ${projectedQty < 1 ? 'pw-pos-danger' : projectedQty < existingQty ? 'pw-pos-warn' : 'pw-pos-ok'}`}>
                      &nbsp;→ {projectedQty < 1 ? 'remove position' : `${projectedQty} shares`}
                    </span>
                  )}
                </span>
              ) : (
                <span className="pw-pos-hint pw-pos-new">
                  {(displaySymbol || symbolId || '').toUpperCase()} · New position
                </span>
              )}
            </div>
            <div className="pw-form-row pw-position-row">
              <input
                type="number"
                value={buyPrice}
                onChange={(e) => setBuyPrice(e.target.value)}
                placeholder="Price"
                step="0.01"
                min="0"
                className="symbol-input pw-buy-price"
                title="Validated against day's trading range"
              />
              <input
                type="number"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder={isExisting ? '±Qty' : 'Qty'}
                step="1"
                className="symbol-input pw-qty-input"
                title={isExisting ? 'Positive to add shares, negative to reduce' : 'Number of shares to add (min 1)'}
              />
              <select
                value={actionType}
                onChange={(e) => setActionType(e.target.value)}
                className="symbol-input pw-action-select"
              >
                <option value="long">Long</option>
                <option value="short">Short</option>
              </select>
              <div className="date-picker-wrapper">
                <DatePicker
                  selected={buyDate}
                  onChange={(date) => setBuyDate(date)}
                  includeDates={allTradeDates}
                  filterDate={date => allTradeDates.some(d => d.getTime() === date.getTime())}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Trade Date"
                  showMonthDropdown
                  showYearDropdown
                  className="date-picker"
                />
                <ChevronDown className="date-picker-icon" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const finishPortfolioSuccess = (listName, action, portfolioMap) => {
    if (portfolioMap) {
      updateCachedPortfolioMap(user.email, portfolioMap);
      setExistingListMap(portfolioMap);
    }
    clearCachedPortfolioData(user.email, listName);
    setLastAction(action);
    setAddedListName(listName);
    setPendingRemoveConfirm(null);
    setStep('success');
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemoveConfirm || !user) return;
    const { listName, compositeKey } = pendingRemoveConfirm;
    setIsSubmitting(true);
    setErrorMsg('');
    try {
      const res = await authFetch(`/api/portfolio/${user.email}/remove-multiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: listName, symbols: [compositeKey] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to remove position');
      finishPortfolioSuccess(listName, 'removed', data.portfolioMap);
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addToList = async (listName, type, priceVal, dateVal, qtyVal, actionVal) => {
    if (!user) { setErrorMsg('Please log in'); return; }
    if (!listName) { setErrorMsg('No list selected'); return; }
    setIsSubmitting(true);
    setErrorMsg('');
    try {
      if (type === 'portfolio') {
        const currentDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
        const formattedBuyDate = dateVal ? dateVal.toISOString().split('T')[0] : null;
        const price = parseFloat(priceVal) || currentPrice || 0;
        const quantity = parseInt(qtyVal, 10);
        const action = actionVal || 'long';
        const compositeKey = `${symbolId}|${action}`;

        if (isNaN(quantity) || quantity === 0) {
          throw new Error('Enter a non-zero quantity');
        }

        const positions = existingListMap[listName] || {};
        const existingEntry = positions[compositeKey];

        if (existingEntry) {
          const existingQty = parseInt(existingEntry[3], 10);
          const finalQty = existingQty + quantity;
          if (finalQty < 1) {
            setPendingRemoveConfirm({ listName, compositeKey, existingQty, quantity, finalQty });
            setIsSubmitting(false);
            return;
          }
          let res;
          if (priceVal && dateVal) {
            res = await authFetch(`/api/portfolio/${user.email}/update-position`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: listName,
                symbol_id: symbolId,
                additional_quantity: quantity,
                new_price: price,
                buy_date: formattedBuyDate,
                action_type: action,
              }),
            });
          } else {
            res = await authFetch(`/api/portfolio/${user.email}/update-quantity`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: listName,
                symbol_id: symbolId,
                action_type: action,
                additional_quantity: quantity,
              }),
            });
          }
          const data = await res.json();
          if (!res.ok) throw new Error(data.message || 'Failed to update portfolio');
          finishPortfolioSuccess(listName, 'updated', data.portfolioMap);
          return;
        } else {
          if (quantity < 1) throw new Error('Number of shares must be at least 1 for a new position');
          if (!formattedBuyDate) throw new Error('Select a trade date');
          const payload = { name: listName, [symbolId]: [price, formattedBuyDate, currentDate, quantity, action] };
          const res = await authFetch(`/api/portfolio/${user.email}/add-multiple`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.message || 'Failed to add to portfolio');
          finishPortfolioSuccess(listName, 'added', data.portfolioMap);
          return;
        }
      } else {
        const payload = { name: listName, symbols: [symbolId] };
        const res = await authFetch(`/api/watchlist/${user.email}/add-multiple`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to add to watchlist');
        if (data.watchlistMap) updateCachedWatchlistMap(user.email, data.watchlistMap);
        clearCachedWatchlistData(user.email, listName);
      }
      setLastAction('added');
      setAddedListName(listName);
      setStep('success');
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAndAdd = async () => {
    if (!user) return;
    setIsSubmitting(true);
    setErrorMsg('');
    const name = newListName.trim() || generateDefaultName(listType);
    try {
      const createUrl = listType === 'portfolio'
        ? `/api/portfolio/${user.email}/create`
        : `/api/watchlist/${user.email}/create`;
      const createRes = await authFetch(createUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const createData = await createRes.json();
      if (!createRes.ok) throw new Error(createData.message || 'Failed to create list');
      if (listType === 'portfolio') {
        updateCachedPortfolioMap(user.email, createData.portfolioMap || {});
      } else {
        updateCachedWatchlistMap(user.email, createData.watchlistMap || {});
      }
      await addToList(name, listType, buyPrice, buyDate, qty, actionType);
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong');
      setIsSubmitting(false);
    }
  };

  const handleSelectExisting = (name) => {
    setSelectedList(name);
    setErrorMsg('');
  };

  const handleAddToSelected = async () => {
    if (!selectedList || !listType) return;
    await addToList(selectedList, listType, buyPrice, buyDate, qty, actionType);
  };

  if (!user) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="pw-modal add-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="pw-modal-content">
            <button className="add-dialog-close" onClick={onClose}><X size={18} /></button>
            <h3>Sign in Required</h3>
            <p className="pw-modal-description">Please log in to add stocks to your portfolio or watchlist.</p>
            <div className="pw-modal-actions">
              <button onClick={onClose} className="pw-btn pw-btn-primary">OK</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="pw-modal add-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="pw-modal-content">
          <button className="add-dialog-close" onClick={onClose}><X size={18} /></button>

          {step === 'choose' && (
            <>
              <h3>Add {(displaySymbol || '').toUpperCase()}</h3>
              <p className="pw-modal-description">Where would you like to add this stock?</p>
              <div className="add-dialog-type-buttons">
                <button className="add-dialog-type-btn" onClick={() => handleChooseType('portfolio')}>
                  <Briefcase size={22} />
                  <span>Portfolio</span>
                </button>
                <button className="add-dialog-type-btn" onClick={() => handleChooseType('watchlist')}>
                  <Eye size={22} />
                  <span>Watchlist</span>
                </button>
              </div>
            </>
          )}

          {step === 'selectList' && (
            <>
              <h3>Add to {listType === 'portfolio' ? 'Portfolio' : 'Watchlist'}</h3>

              {availableLists.length > 0 && (
                <div className="add-dialog-list-section">
                  <p className="add-dialog-section-label">Select an existing {listType === 'portfolio' ? 'portfolio' : 'watchlist'}:</p>
                  <div className="add-dialog-list-options">
                    {availableLists.map((name) => (
                      <button
                        key={name}
                        className={`add-dialog-list-option ${selectedList === name ? 'selected' : ''}`}
                        onClick={() => handleSelectExisting(name)}
                      >
                        {listType === 'portfolio' ? <Briefcase size={15} /> : <Eye size={15} />}
                        <span>{name}</span>
                        {selectedList === name && <Check size={15} className="add-dialog-check" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {listType === 'portfolio' && renderPortfolioFields(selectedList)}

              {allExistingListsAreFull && (
                <div className="add-dialog-list-section">
                  <p className="add-dialog-error">
                    Max symbols in all existing {listType === 'portfolio' ? 'portfolios' : 'watchlists'}.
                  </p>
                  <div className="add-dialog-success-actions">
                    <a
                      href={listType === 'portfolio' ? '/portfolio' : '/watchlist'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pw-btn pw-btn-primary"
                      onClick={onClose}
                    >
                      Go to {listType === 'portfolio' ? 'Portfolio' : 'Watchlist'} <ExternalLink size={14} />
                    </a>
                  </div>
                </div>
              )}

              {canCreateNewList && (
                <>
                  <div className="add-dialog-divider">
                    <span>or</span>
                  </div>
                  <button className="add-dialog-create-new-btn" onClick={() => setStep('createNew')}>
                    <Plus size={16} /> Create new {listType === 'portfolio' ? 'portfolio' : 'watchlist'}
                  </button>
                </>
              )}

              {errorMsg && <p className="add-dialog-error">{errorMsg}</p>}

              <div className="pw-modal-actions">
                <button onClick={() => { setStep('choose'); setErrorMsg(''); }} className="pw-btn pw-btn-ghost">Back</button>
                <button
                  onClick={handleAddToSelected}
                  className="pw-btn pw-btn-primary"
                  disabled={
                    !selectedList
                    || isSubmitting
                    || availableLists.length === 0
                    || (listType === 'portfolio' && !canSubmitPortfolio(selectedList))
                  }
                >
                  {isSubmitting
                    ? (listType === 'portfolio' && getExistingEntry(selectedList) ? 'Updating…' : 'Adding…')
                    : (listType === 'portfolio' && getExistingEntry(selectedList) ? 'Update' : 'Add')}
                </button>
              </div>
            </>
          )}

          {step === 'createNew' && (
            <>
              <h3>New {listType === 'portfolio' ? 'Portfolio' : 'Watchlist'}</h3>
              <p className="pw-modal-description">Give it a name, or leave empty for a default.</p>
              <input
                type="text"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder={listType === 'portfolio' ? 'e.g. Growth Stocks' : 'e.g. Tech Stocks'}
                className="pw-modal-input"
              />

              {listType === 'portfolio' && renderPortfolioFields(null)}

              {errorMsg && <p className="add-dialog-error">{errorMsg}</p>}

              <div className="pw-modal-actions">
                <button onClick={() => { setStep('selectList'); setErrorMsg(''); }} className="pw-btn pw-btn-ghost">Back</button>
                <button
                  onClick={handleCreateAndAdd}
                  className="pw-btn pw-btn-primary"
                  disabled={isSubmitting || (listType === 'portfolio' && !canSubmitPortfolio(null))}
                >
                  {isSubmitting ? 'Creating...' : 'Create & Add'}
                </button>
              </div>
            </>
          )}

          {step === 'success' && (
            <div className="add-dialog-success">
              <div className="add-dialog-success-icon"><Check size={28} /></div>
              <h3>
                {(displaySymbol || '').toUpperCase()}{' '}
                {lastAction === 'removed' ? 'removed' : lastAction === 'updated' ? 'updated' : 'added'}!
              </h3>
              <p className="pw-modal-description">
                {lastAction === 'removed' ? 'Removed from' : lastAction === 'updated' ? 'Updated in' : 'Successfully added to'}{' '}
                <strong>{addedListName}</strong>
              </p>
              <div className="add-dialog-success-actions">
                <a
                  href={listType === 'portfolio' ? '/portfolio' : '/watchlist'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pw-btn pw-btn-primary"
                  onClick={onClose}
                >
                  Go to {listType === 'portfolio' ? 'Portfolio' : 'Watchlist'} <ExternalLink size={14} />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {pendingRemoveConfirm && (
        <div className="modal-overlay add-dialog-confirm-overlay" onClick={() => !isSubmitting && setPendingRemoveConfirm(null)}>
          <div className="pw-modal pw-modal-warn" onClick={(e) => e.stopPropagation()}>
            <div className="pw-modal-content">
              <h3 className="pw-modal-warn-title">Position Quantity Would Drop Below 1</h3>
              <p className="pw-modal-description">
                This change would reduce the position to {pendingRemoveConfirm.finalQty} shares.
                Remove {(displaySymbol || symbolId || '').toUpperCase()} from <strong>{pendingRemoveConfirm.listName}</strong>, or go back to adjust the quantity.
              </p>
              <ul className="pw-modal-qty-list">
                <li className="pw-modal-qty-item">
                  <span className="pw-modal-qty-symbol">{(displaySymbol || symbolId || '').toUpperCase()}</span>
                  <span className="pw-modal-qty-math">
                    {pendingRemoveConfirm.existingQty} + ({pendingRemoveConfirm.quantity}) ={' '}
                    <strong className="pw-pos-danger">{pendingRemoveConfirm.finalQty}</strong>
                  </span>
                </li>
              </ul>
              <div className="pw-modal-actions">
                <button
                  onClick={() => setPendingRemoveConfirm(null)}
                  className="pw-btn pw-btn-secondary"
                  disabled={isSubmitting}
                >
                  Go Back
                </button>
                <button
                  onClick={handleConfirmRemove}
                  className="pw-btn pw-btn-danger"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Removing…' : 'Remove & Continue'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const StockDetail = () => {
  const { symbolId: rawSymbolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const symbolId = rawSymbolId ? String(rawSymbolId).toUpperCase() : rawSymbolId;
  const [stockData, setStockData] = useState(null);
  /** Last known premarket / afterhours quotes for this symbol (icon-only pills). */
  const [extSessionQuotes, setExtSessionQuotes] = useState({ premarket: null, afterhours: null });
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('news');
  const [financialsData, setFinancialsData] = useState(null);
  const [financialsLoading, setFinancialsLoading] = useState(false);
  const [financialsError, setFinancialsError] = useState(null);
  const [financialsSubTab, setFinancialsSubTab] = useState('income');
  const [financialsPeriodSubTab, setFinancialsPeriodSubTab] = useState('fullYear');
  const [summarySubTab, setSummarySubTab] = useState('summary');
  const [metricRatiosData, setMetricRatiosData] = useState(null);
  const [metricRatiosLoading, setMetricRatiosLoading] = useState(false);
  const [metricRatiosError, setMetricRatiosError] = useState(null);
  const [aiOverviewData, setAiOverviewData] = useState(null);
  const [aiOverviewLoading, setAiOverviewLoading] = useState(true);
  const [aiOverviewError, setAiOverviewError] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);

  useEffect(() => {
    if (!rawSymbolId || rawSymbolId === symbolId) return;
    navigate(`/stock/${encodeURIComponent(symbolId)}${location.search}${location.hash}`, { replace: true });
  }, [rawSymbolId, symbolId, navigate, location.search, location.hash]);

  useEffect(() => {
    const fetchStockData = async () => {
      try {
        const response = await authFetch(`/api/stocks/${encodeURIComponent(symbolId)}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError(`Stock data for ${symbolId} not found`);
            return;
          }
          throw new Error('Failed to fetch stock data');
        }
        const data = await response.json();
        // Full payload: { stock, income_statement, balance_sheet, cashflow, metric_ratio, company_overview }
        if (data.stock) {
          setStockData(data.stock);
          setFinancialsData({
            income_statement: data.income_statement || [],
            balance_sheet: data.balance_sheet || [],
            cashflow: data.cashflow || []
          });
          setMetricRatiosData(data.metric_ratio != null && data.metric_ratio.length > 0 ? { metric_ratio: data.metric_ratio } : null);
          setAiOverviewData(data.company_overview || null);
          setAiOverviewError(null);
          setFinancialsLoading(false);
          setMetricRatiosLoading(false);
          setAiOverviewLoading(false);
        } else {
          // Legacy shape: response is the stock object (e.g. with news)
          setStockData(data);
          setAiOverviewLoading(false);
        }
      } catch (err) {
        console.error('Error fetching stock data:', err);
        setError(err.message);
        setAiOverviewError(err.message);
        setAiOverviewLoading(false);
      }
    };

    fetchStockData();
  }, [symbolId]);

  useLiveMarketDisplay({
    enabled: Boolean(symbolId && stockData),
    symbolIds: symbolId ? [symbolId] : null,
    onApply: (live) => {
      const quote = live.quotes ? live.quotes[symbolId] : null;
      const extQuote = live.extendedQuotes ? live.extendedQuotes[symbolId] : null;
      const premarket = live.premarketQuotes ? live.premarketQuotes[symbolId] : null;
      const afterhours = live.afterhoursQuotes ? live.afterhoursQuotes[symbolId] : null;
      if (premarket || afterhours) {
        setExtSessionQuotes((prev) => ({
          premarket: premarket || prev.premarket,
          afterhours: afterhours || prev.afterhours,
        }));
      }
      if (!quote && !extQuote) return;
      setStockData((prev) => (prev ? applyLiveQuoteToStock(prev, quote, extQuote) : prev));
    }
  });

  useEffect(() => {
    setFinancialsData(null);
    setFinancialsError(null);
    setMetricRatiosData(null);
    setMetricRatiosError(null);
    setAiOverviewData(null);
    setAiOverviewError(null);
    setAiOverviewLoading(true);
    setExtSessionQuotes({ premarket: null, afterhours: null });
  }, [symbolId]);

  if (error) {
    return (
      <div className="sd-page-state">
        <div className="sd-page-state-card sd-page-state-error">
          <Info size={26} />
          <h3>Unable to load stock</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!stockData) {
    return (
      <div className="stock-detail stock-detail-modern sd-loading-shell">
        <div className="sd-skeleton-block sd-skeleton-block--xl" />
        <div className="sd-skeleton-block sd-skeleton-block--lg" />
        <div className="sd-skeleton-block" />
      </div>
    );
  }

  const formatNumber = (value, decimals = 2) => {
    return value != null ? Number(value).toFixed(decimals) : '--';
  };

  const formatCurrency = (value, decimals = 2) => {
    return value != null ? `${Number(value).toFixed(decimals)}` : '--';
  };

  const formatPercentage = (value) => {
    return value != null ? `${Number(value).toFixed(2)}%` : '--';
  };

  const formatMarketCap = (marketCap) => {
    if (marketCap == null) return '--';
    if (marketCap >= 1000000000000) {
      return `${(marketCap / 1000000000000).toFixed(2)}T`;
    } else if (marketCap >= 1000000000) {
      return `${(marketCap / 1000000000).toFixed(2)}B`;
    } else if (marketCap >= 1000000) {
      return `${(marketCap / 1000000).toFixed(2)}M`;
    } else if (marketCap >= 1000) {
      return `${(marketCap / 1000).toFixed(2)}K`;
    } else {
      return `${marketCap.toFixed(2)}`;
    }
  };

  const aiOverviewAvailable = !aiOverviewLoading && !aiOverviewError && Boolean(aiOverviewData);
  const isGuestUser = !getAccessToken();
  const guestFinancialsGated = isGuestUser && financialsData && !(
    (financialsData.income_statement || []).length ||
    (financialsData.balance_sheet || []).length ||
    (financialsData.cashflow || []).length
  );
  const guestMetricsGated = isGuestUser && !(metricRatiosData?.metric_ratio?.length);
  const guestAiGated = isGuestUser && !aiOverviewData;

  const formatVolume = (v) => {
    if (v == null) return '--';
    const n = Number(v);
    if (Number.isNaN(n)) return '--';
    if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
    return n.toLocaleString();
  };

  const priceChange = (stockData.close != null && stockData.prev_close != null)
    ? Number(stockData.close) - Number(stockData.prev_close)
    : null;
  const pctChange = stockData.pct_change != null ? Number(stockData.pct_change) : null;
  const direction = priceChange == null
    ? 'flat'
    : priceChange > 0
      ? 'up'
      : priceChange < 0
        ? 'down'
        : 'flat';
  const directionIcon = direction === 'up'
    ? <TrendingUp size={16} />
    : direction === 'down'
      ? <TrendingDown size={16} />
      : <Minus size={16} />;

  const exchange = stockData.exchange
    || (stockData.symbol_id ? stockData.symbol_id.split('_')[1] : '');

  // Extended-hours quote — only the active session (pre-market or after-hours), never both.
  // Hidden during regular hours so the main price stays the last regular-session quote.
  // After the after-hours window (and on weekends) keep showing after-hours until pre-market starts.
  const marketSession = getNyMarketSession();
  const buildExtPill = (sessionKey, quote) => {
    if (!quote) return null;
    const price = quote.price_ext != null ? Number(quote.price_ext) : null;
    if (!Number.isFinite(price)) return null;
    const change = quote.change_ext != null ? Number(quote.change_ext) : null;
    const pct = quote.pct_change_ext != null ? Number(quote.pct_change_ext) : null;
    const direction = change == null ? 'flat' : change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
    return {
      key: sessionKey,
      label: sessionKey === 'premarket' ? 'Pre-Market' : 'After Hours',
      Icon: sessionKey === 'premarket' ? Sunrise : Moon,
      price,
      change,
      pct,
      direction,
    };
  };
  const activeExtSession = marketSession === 'premarket'
    ? 'premarket'
    : (marketSession === 'afterhours' || marketSession === 'closed')
      ? 'afterhours'
      : null;
  const extPills = [];
  if (activeExtSession) {
    const liveQuote = extSessionQuotes[activeExtSession];
    const livePill = buildExtPill(activeExtSession, liveQuote);
    if (livePill) {
      extPills.push(livePill);
    } else {
      const dataSession = stockData.ext_session === 'afterhours'
        ? 'afterhours'
        : stockData.ext_session === 'premarket'
          ? 'premarket'
          : activeExtSession;
      if (dataSession === activeExtSession) {
        const fallback = buildExtPill(activeExtSession, {
          price_ext: stockData.price_ext,
          change_ext: stockData.change_ext,
          pct_change_ext: stockData.pct_change_ext,
        });
        if (fallback) extPills.push(fallback);
      }
    }
  }

  // Build a 52-week range pointer position (0–100%).
  const lo = Number(stockData.low_prc_52weeks);
  const hi = Number(stockData.high_prc_52weeks);
  const cur = Number(stockData.close);
  const rangePct = (Number.isFinite(lo) && Number.isFinite(hi) && Number.isFinite(cur) && hi > lo)
    ? Math.max(0, Math.min(100, ((cur - lo) / (hi - lo)) * 100))
    : null;

  const SUMMARY_GROUPS = [
    {
      key: 'trading',
      title: 'Trading Activity',
      icon: <Activity size={15} />,
      items: [
        { label: 'Open', value: formatNumber(stockData.open) },
        { label: 'Previous Close', value: formatCurrency(stockData.prev_close) },
        { label: 'Volume', value: formatVolume(stockData.volume) },
        { label: 'Avg. Volume', value: formatVolume(stockData.average_volume) },
        { label: 'Last Earnings', value: stockData.last_earn_date || '--' },
      ],
    },
    {
      key: 'valuation',
      title: 'Valuation',
      icon: <BadgeDollarSign size={15} />,
      items: [
        { label: 'Market Cap', value: formatMarketCap(stockData.market_cap) },
        { label: 'P/E Ratio', value: formatNumber(stockData.pe) },
        { label: 'EPS', value: formatNumber(stockData.eps) },
        { label: 'Dividend', value: formatNumber(stockData.dividends) },
        { label: 'Next Earnings', value: stockData.next_earn_date || '--' },
      ],
    },
    {
      key: 'technical',
      title: 'Technical',
      icon: <CircleGauge size={15} />,
      items: [
        { label: 'RSI', value: formatNumber(stockData.rsi) },
        { label: 'Beta', value: formatNumber(stockData.beta) },
        { label: 'MA20D', value: formatNumber(stockData.sma20_prc) },
        { label: 'MA50D', value: formatNumber(stockData.sma50_prc) },
        { label: 'MA200D', value: formatNumber(stockData.sma200_prc) },
      ],
    },
    {
      key: 'performance',
      title: 'Performance',
      icon: <LineChart size={15} />,
      items: [
        { label: '1-Week', value: formatPercentage(stockData.pct_change_5days), tone: 'pct', raw: stockData.pct_change_5days },
        { label: '1-Month', value: formatPercentage(stockData.pct_change_20days), tone: 'pct', raw: stockData.pct_change_20days },
        { label: '3-Month', value: formatPercentage(stockData.pct_change_60days), tone: 'pct', raw: stockData.pct_change_60days },
        { label: '6-Month', value: formatPercentage(stockData.pct_change_120days), tone: 'pct', raw: stockData.pct_change_120days },
        { label: '52W Range', value: `${formatCurrency(stockData.low_prc_52weeks)} – ${formatCurrency(stockData.high_prc_52weeks)}` },
      ],
    },
  ];

  const toneClass = (raw) => {
    if (raw == null || raw === '') return '';
    const n = Number(raw);
    if (Number.isNaN(n)) return '';
    if (n > 0) return 'sd-stat-up';
    if (n < 0) return 'sd-stat-down';
    return '';
  };

  return (
    <div className="stock-detail stock-detail-modern">
      {/* HERO HEADER */}
      <header className="sd-hero">
        <div className="sd-hero-top">
          <div className="sd-symbol-block">
            <div className="sd-symbol-meta">
              <div className="sd-symbol-line">
                <h1 className="sd-symbol-ticker">{stockData.symbol}</h1>
                {exchange && <span className="sd-exchange-badge">{exchange}</span>}
              </div>
              <p className="sd-company-name">{stockData.name || 'Unknown company'}</p>
            </div>
          </div>
          <button
            className="sd-add-cta"
            onClick={() => setShowAddDialog(true)}
            title="Add to Portfolio or Watchlist"
            aria-label="Add to Portfolio or Watchlist"
          >
            <Plus size={16} />
            <span>Add to list</span>
          </button>
        </div>

        <div className="sd-hero-price-row">
          <div className="sd-price-main">
            <div className="sd-price-value">
              {formatNumber(stockData.close)}
            </div>
            <div className={`sd-change-pill sd-change-${direction}`}>
              {directionIcon}
              <span>
                {priceChange != null ? `${priceChange >= 0 ? '+' : ''}${priceChange.toFixed(2)}` : '--'}
                {' '}
                ({pctChange != null ? `${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(2)}%` : '--'})
              </span>
            </div>
            {extPills.map(({ key, label, Icon, price, change, pct, direction: extDir }) => (
              <div
                key={key}
                className={`sd-ext-pill sd-change-${extDir}`}
                title={`${label} price vs last regular close`}
                aria-label={`${label} ${price.toFixed(2)}`}
              >
                <Icon size={14} aria-hidden="true" />
                <span className="sd-ext-price">{price.toFixed(2)}</span>
                <span>
                  {change != null ? `${change >= 0 ? '+' : ''}${change.toFixed(2)}` : '--'}
                  {' '}
                  ({pct != null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%` : '--'})
                </span>
              </div>
            ))}
          </div>

          {rangePct != null && (
            <div className="sd-range-block" aria-label="52-week price range">
              <div className="sd-range-labels">
                <span>52W Low</span>
                <span>52W High</span>
              </div>
              <div className="sd-range-track">
                <div className="sd-range-fill" style={{ width: `${rangePct}%` }} />
                <div className="sd-range-marker" style={{ left: `${rangePct}%` }} />
              </div>
              <div className="sd-range-bounds">
                <span>{lo.toFixed(2)}</span>
                <span>{hi.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* CHART */}
      <section className="sd-card sd-chart-card">
        <div className="sd-card-header">
          <h3 className="sd-card-title">
            <LineChart size={18} className="sd-card-title-icon" />
            Price Chart
          </h3>
        </div>
        <div className="sd-tradeview-wrap">
          <TradeView symbol={`${stockData.symbol_id.split('_')[1]}:${stockData.symbol_id.split('_')[0].replace(/-/g, '.')}`} />
        </div>
      </section>

      {/* OVERVIEW: Summary / Key Metrics / AI */}
      <section className="sd-card sd-overview-card">
        <div className="sd-subtab-bar" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={summarySubTab === 'summary'}
            className={`sd-subtab ${summarySubTab === 'summary' ? 'is-active' : ''}`}
            onClick={() => setSummarySubTab('summary')}
          >
            <Activity size={15} />
            <span>Summary</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={summarySubTab === 'keyMetrics'}
            className={`sd-subtab ${summarySubTab === 'keyMetrics' ? 'is-active' : ''}`}
            onClick={() => setSummarySubTab('keyMetrics')}
          >
            <Gauge size={15} />
            <span>Key Metrics</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={summarySubTab === 'aiOverview'}
            className={`sd-subtab ${summarySubTab === 'aiOverview' ? 'is-active' : ''} ${aiOverviewAvailable ? 'has-ai-overview' : ''}`}
            onClick={() => setSummarySubTab('aiOverview')}
            title={aiOverviewAvailable ? 'AI overview available' : undefined}
          >
            <Sparkles
              size={15}
              className="sd-ai-tab-icon"
              fill={aiOverviewAvailable ? '#7dd3fc' : 'none'}
            />
            <span>AI Overview</span>
          </button>
        </div>

        <div className="sd-subtab-panel">
          {summarySubTab === 'summary' && (
            <div className="sd-summary-grid">
              {SUMMARY_GROUPS.map((grp) => (
                <div key={grp.key} className="sd-summary-group">
                  <div className="sd-summary-group-title">
                    <span className="sd-summary-group-icon">{grp.icon}</span>
                    {grp.title}
                  </div>
                  <div className="sd-summary-stat-list">
                    {grp.items.map((it) => (
                      <div key={it.label} className="sd-summary-stat">
                        <span className="sd-summary-stat-label">{it.label}</span>
                        <span className={`sd-summary-stat-value ${it.tone === 'pct' ? toneClass(it.raw) : ''}`}>
                          {it.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {summarySubTab === 'keyMetrics' && (
            <KeyMetricsTab
              data={metricRatiosData}
              loading={metricRatiosLoading}
              error={metricRatiosError}
              formatFinancialValue={formatFinancialValue}
              guestGated={guestMetricsGated}
            />
          )}
          {summarySubTab === 'aiOverview' && (
            <AIOverviewTab
              data={aiOverviewData}
              loading={aiOverviewLoading}
              error={aiOverviewError}
              guestGated={guestAiGated}
            />
          )}
        </div>
      </section>

      {!guestFinancialsGated && <FundamentalCharts financialsData={financialsData} />}

      {/* MAIN: News / Financials / Profile */}
      <section className="sd-card sd-tabs-card">
        <div className="sd-maintab-bar" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'news'}
            className={`sd-maintab ${activeTab === 'news' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('news')}
          >
            <Newspaper size={16} /> <span>News</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'financials'}
            className={`sd-maintab ${activeTab === 'financials' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('financials')}
          >
            <FileText size={16} /> <span>Financials</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'profile'}
            className={`sd-maintab ${activeTab === 'profile' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <Building2 size={16} /> <span>Profile</span>
          </button>
        </div>

        <div className="sd-maintab-panel">
          {activeTab === 'profile' && (
            <div className="sd-profile">
              <div className="sd-profile-description">
                <h4 className="sd-profile-section-title">About {stockData.name}</h4>
                <p>{stockData.profile || 'No company description available.'}</p>
              </div>
              <div className="sd-profile-info-grid">
                <div className="sd-profile-info-item">
                  <span className="sd-profile-info-icon"><Briefcase size={14} /></span>
                  <span className="sd-profile-info-label">Industry</span>
                  <span className="sd-profile-info-value">{stockData.industry || '--'}</span>
                </div>
                <div className="sd-profile-info-item">
                  <span className="sd-profile-info-icon"><BarChart3 size={14} /></span>
                  <span className="sd-profile-info-label">Sector</span>
                  <span className="sd-profile-info-value">{stockData.sector || '--'}</span>
                </div>
                <div className="sd-profile-info-item">
                  <span className="sd-profile-info-icon"><Calendar size={14} /></span>
                  <span className="sd-profile-info-label">IPO Date</span>
                  <span className="sd-profile-info-value">{stockData.ipo_date || '--'}</span>
                </div>
                <div className="sd-profile-info-item">
                  <span className="sd-profile-info-icon"><Building2 size={14} /></span>
                  <span className="sd-profile-info-label">Exchange</span>
                  <span className="sd-profile-info-value">{stockData.exchange || '--'}</span>
                </div>
                <div className="sd-profile-info-item">
                  <span className="sd-profile-info-icon"><Users size={14} /></span>
                  <span className="sd-profile-info-label">CEO</span>
                  <span className="sd-profile-info-value">{stockData.ceo || '--'}</span>
                </div>
                <div className="sd-profile-info-item">
                  <span className="sd-profile-info-icon"><Users size={14} /></span>
                  <span className="sd-profile-info-label">Employees</span>
                  <span className="sd-profile-info-value">
                    {stockData.employees ? parseInt(stockData.employees).toLocaleString() : '--'}
                  </span>
                </div>
                <div className="sd-profile-info-item">
                  <span className="sd-profile-info-icon"><MapPin size={14} /></span>
                  <span className="sd-profile-info-label">Headquarters</span>
                  <span className="sd-profile-info-value">
                    {[stockData.address, stockData.city, stockData.state, stockData.country]
                      .filter(Boolean).join(', ') || '--'}
                  </span>
                </div>
                <div className="sd-profile-info-item">
                  <span className="sd-profile-info-icon"><Globe size={14} /></span>
                  <span className="sd-profile-info-label">Website</span>
                  <span className="sd-profile-info-value">
                    {stockData.website ? (
                      <a
                        href={stockData.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sd-profile-link"
                      >
                        {stockData.website} <ExternalLink size={12} />
                      </a>
                    ) : '--'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'financials' && (
            <div className="sd-financials">
              {guestFinancialsGated && (
                <MemberSignupCTA
                  title="Financial statements for members"
                  description="Log in or sign up to access income statements, balance sheets, cash flows, and financial charts."
                />
              )}
              {financialsLoading && (
                <div className="sd-tab-state">
                  <div className="sd-skeleton-block sd-skeleton-block--lg" />
                  <div className="sd-skeleton-block" />
                </div>
              )}
              {financialsError && <p className="sd-tab-error">{financialsError}</p>}
              {!financialsLoading && !financialsError && financialsData && !guestFinancialsGated && (
                <>
                  <div className="sd-financials-toolbar">
                    <div className="sd-segmented" role="tablist" aria-label="Statement type">
                      <button
                        type="button"
                        className={`sd-segmented-btn ${financialsSubTab === 'income' ? 'is-active' : ''}`}
                        onClick={() => setFinancialsSubTab('income')}
                      >
                        Income
                      </button>
                      <button
                        type="button"
                        className={`sd-segmented-btn ${financialsSubTab === 'balance' ? 'is-active' : ''}`}
                        onClick={() => setFinancialsSubTab('balance')}
                      >
                        Balance Sheet
                      </button>
                      <button
                        type="button"
                        className={`sd-segmented-btn ${financialsSubTab === 'cashflow' ? 'is-active' : ''}`}
                        onClick={() => setFinancialsSubTab('cashflow')}
                      >
                        Cash Flow
                      </button>
                    </div>
                  </div>
                  {(() => {
                    const reportKey = financialsSubTab === 'income'
                      ? 'income_statement'
                      : financialsSubTab === 'balance'
                        ? 'balance_sheet'
                        : 'cashflow';
                    const rows = financialsData[reportKey] || [];
                    const { fullYear, quarterly } = sortPeriods(rows);
                    const periods = financialsPeriodSubTab === 'fullYear' ? fullYear : quarterly;
                    const allPeriods = [...fullYear, ...quarterly];
                    const reportCurrency = allPeriods.find((p) => p.reported_currency)?.reported_currency || null;
                    const lineItems = getLineItemsForReport(reportKey, periods);
                    return (
                      <>
                        <div className="sd-financials-period-bar">
                          <div className="sd-segmented sd-segmented--sm">
                            <button
                              type="button"
                              className={`sd-segmented-btn ${financialsPeriodSubTab === 'fullYear' ? 'is-active' : ''}`}
                              onClick={() => setFinancialsPeriodSubTab('fullYear')}
                            >
                              Annual
                            </button>
                            <button
                              type="button"
                              className={`sd-segmented-btn ${financialsPeriodSubTab === 'quarterly' ? 'is-active' : ''}`}
                              onClick={() => setFinancialsPeriodSubTab('quarterly')}
                            >
                              Quarterly
                            </button>
                          </div>
                          {reportCurrency && (
                            <span className="financial-currency-badge">Currency: {reportCurrency}</span>
                          )}
                        </div>
                        {!periods || periods.length === 0 ? (
                          <div className="sd-tab-empty">
                            <FileText size={26} className="sd-tab-empty-icon" />
                            <p>
                              No {financialsPeriodSubTab === 'fullYear' ? 'annual' : 'quarterly'} data available for this report.
                            </p>
                          </div>
                        ) : (
                          <FinancialPeriodTable periods={periods} lineItems={lineItems} />
                        )}
                      </>
                    );
                  })()}
                </>
              )}
              {!financialsLoading && !financialsError && !financialsData && activeTab === 'financials' && !guestFinancialsGated && (
                <div className="sd-tab-empty">
                  <FileText size={26} className="sd-tab-empty-icon" />
                  <p>No financial data available for this symbol.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'news' && (
            <div className="sd-news">
              {stockData.news && stockData.news.length > 0 ? (
                <ul className="sd-news-list">
                  {stockData.news.map((item, index) => {
                    const sourceCode = item.news_id ? item.news_id.slice(0, 2) : '';
                    const source = sourceCode === 'GN'
                      ? 'GlobeNewswire'
                      : sourceCode === 'BW'
                        ? 'Businesswire'
                        : 'PR NewsWire';
                    return (
                      <li key={index} className="sd-news-item">
                        <a
                          className="sd-news-link"
                          href={item.weburl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <div className="sd-news-meta">
                            <span className="sd-news-source">{source}</span>
                            <span className="sd-news-dot">•</span>
                            <span className="sd-news-time">{item.pub_datetime}</span>
                          </div>
                          <h4 className="sd-news-headline">
                            {item.headline.substring(0, 220) + (item.headline.length > 220 ? '…' : '')}
                          </h4>
                          <span className="sd-news-cta">
                            Read article <ExternalLink size={12} />
                          </span>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="sd-tab-empty">
                  <Newspaper size={26} className="sd-tab-empty-icon" />
                  <p>No recent news available for this stock.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {showAddDialog && (
        <AddToListDialog
          symbolId={stockData.symbol_id}
          displaySymbol={stockData.symbol}
          currentPrice={stockData.close}
          onClose={() => setShowAddDialog(false)}
        />
      )}
    </div>
  );
};

export default StockDetail;